// ================================================
// Tenant Status Guard - Frontend Classic
// Redirects to maintenance page if tenant is inactive
// ================================================

import React, { useEffect, useState } from 'react';
import ServiceMaintenance from '../pages/ServiceMaintenance';
import { withApiBaseUrl } from '../lib/apiBaseUrl';

interface TenantStatusGuardProps {
  children: React.ReactNode;
  tenantId?: string;
}

const TenantStatusGuard: React.FC<TenantStatusGuardProps> = ({ children, tenantId }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [tenantStatus, setTenantStatus] = useState<'active' | 'trial' | 'inactive' | null>(null);
  const [isPermanentlyInactive, setIsPermanentlyInactive] = useState(false);

  useEffect(() => {
    const checkTenantStatus = async () => {
      if (!tenantId) {
        setIsChecking(false);
        return;
      }

      try {
        // Keep this guard on a public endpoint that is already exposed in production.
        // We only need a safe existence check here; maintenance/inactive flows are
        // handled elsewhere by dedicated maintenance APIs and server-side controls.
        const response = await fetch(
          withApiBaseUrl(`/api/v1/configuration-service/tenant-content/${tenantId}`),
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.ok) {
          setTenantStatus('active');
          setIsPermanentlyInactive(false);
        } else if (response.status === 404) {
          setTenantStatus('inactive');
        }
      } catch (error) {
        console.error('Failed to check tenant status:', error);
      } finally {
        setIsChecking(false);
      }
    };

    checkTenantStatus();

    // Check status every 5 minutes
    const interval = setInterval(checkTenantStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [tenantId]);

  // Show loading
  if (isChecking) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-dark">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-white">กำลังตรวจสอบสถานะระบบ...</p>
        </div>
      </div>
    );
  }

  // Define blocked statuses that should show maintenance page
  const blockedStatuses = ['inactive', 'suspended', 'blocked', 'banned', 'payment_overdue', 'payment_failed'];
  const isBlocked = blockedStatuses.includes(tenantStatus as string) || isPermanentlyInactive;

  // Show maintenance page for blocked tenants
  if (isBlocked) {
    return <ServiceMaintenance />;
  }

  // Allow access for active/trial tenants only
  return <>{children}</>;
};

export default TenantStatusGuard;

